import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/operator/RichTextEditor";
import { ArrowLeft, Save } from "lucide-react";

interface BlogEditorProps {
  postId?: string | null;
  localePrefix: string;
  onClose: () => void;
  onSaved: () => void;
}

interface BlogFormData {
  title: string;
  slug: string;
  excerpt: string;
  meta_description: string;
  content_html: string;
  author_name: string;
  category: string;
  tags: string;
  featured_image_url: string;
}

const EMPTY_FORM: BlogFormData = {
  title: "",
  slug: "",
  excerpt: "",
  meta_description: "",
  content_html: "<p></p>",
  author_name: "CRAFTFINDUS Redaktion",
  category: "",
  tags: "",
  featured_image_url: "",
};

export default function BlogEditor({ postId, localePrefix, onClose, onSaved }: BlogEditorProps) {
  const [form, setForm] = useState<BlogFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!postId);

  useEffect(() => {
    if (!postId) return;
    (async () => {
      const { data } = await supabase.from("blog_posts").select("*").eq("id", postId).single();
      if (data) {
        setForm({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          meta_description: data.meta_description || "",
          content_html: data.content_html || "<p></p>",
          author_name: data.author_name || "CRAFTFINDUS Redaktion",
          category: data.category || "",
          tags: (data.tags || []).join(", "),
          featured_image_url: data.featured_image_url || "",
        });
      }
      setLoading(false);
    })();
  }, [postId]);

  const set = (key: keyof BlogFormData, value: string) => setForm((p) => ({ ...p, [key]: value }));

  const autoSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue").replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const handleSave = async () => {
    if (!form.title || !form.slug) {
      toast.error("Titel und Slug sind Pflichtfelder");
      return;
    }
    setSaving(true);
    const payload = {
      locale_prefix: localePrefix,
      title: form.title,
      slug: form.slug,
      excerpt: form.excerpt || null,
      meta_description: form.meta_description || null,
      content_html: form.content_html || null,
      author_name: form.author_name || null,
      category: form.category || null,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      featured_image_url: form.featured_image_url || null,
    };

    let error;
    if (postId) {
      ({ error } = await supabase.from("blog_posts").update(payload).eq("id", postId));
    } else {
      ({ error } = await supabase.from("blog_posts").insert(payload));
    }
    setSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(postId ? "Beitrag aktualisiert" : "Beitrag erstellt");
      onSaved();
    }
  };

  if (loading) return <p className="text-caption py-8 text-center">Lädt...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="quiet" size="sm" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <h2 className="text-lg font-semibold">{postId ? "Beitrag bearbeiten" : "Neuer Beitrag"}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Titel *</Label>
          <Input
            value={form.title}
            onChange={(e) => {
              set("title", e.target.value);
              if (!postId) set("slug", autoSlug(e.target.value));
            }}
            placeholder="Was kostet ein Elektriker 2026?"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Slug *</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="was-kostet-ein-elektriker" />
        </div>
        <div className="space-y-1.5">
          <Label>Kategorie</Label>
          <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Kosten, Ratgeber, Förderungen..." />
        </div>
        <div className="space-y-1.5">
          <Label>Autor</Label>
          <Input value={form.author_name} onChange={(e) => set("author_name", e.target.value)} />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Meta Description (max 155 Zeichen)</Label>
          <Textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} maxLength={160} rows={2} />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Kurztext / Excerpt</Label>
          <Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} />
        </div>
        <div className="space-y-1.5">
          <Label>Tags (kommagetrennt)</Label>
          <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Elektriker, Kosten, 2026" />
        </div>
        <div className="space-y-1.5">
          <Label>Bild-URL</Label>
          <Input value={form.featured_image_url} onChange={(e) => set("featured_image_url", e.target.value)} placeholder="https://..." />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Inhalt</Label>
        <RichTextEditor content={form.content_html} onChange={(html) => set("content_html", html)} />
      </div>

      <div className="flex justify-end">
        <Button variant="commit" onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-1" /> {saving ? "Speichert..." : "Speichern"}
        </Button>
      </div>
    </div>
  );
}
